import type { AppTheme } from '@tix-ticketing/ui-theme';
import { useMemo } from 'react';
import type { AppSettingsDto } from 'api/generated';
import { themePresets, type ThemePresetId } from 'lib/app-configs';
import { useAppState } from 'lib/app-state';
import { useLocalStorage } from 'lib/hooks/use-storage';

/** The visual part of a white-label config: what `generateTheme` and
 *  `ThemeMeta` read. */
export type ThemeConfig = {
  theme: AppTheme;
  darkTheme?: AppTheme;
};
const devToolsEnabled = process.env.NEXT_PUBLIC_DEV_QOL === 'true';
const hasEntries = (value: unknown): value is object => !!value && typeof value === 'object' && Object.keys(value).length > 0;
export const isThemePresetId = (value: unknown): value is ThemePresetId => typeof value === 'string' && Object.hasOwn(themePresets, value);

/**
 * `ThemeMeta` calls array and string methods on `fontFaces` while rendering,
 * outside `buildTheme`'s fallback, so a malformed value from `/appsettings`
 * would crash the app. Keep only the string entries, and drop the field when
 * it is not an array.
 */
function sanitizeTheme(theme: object): AppTheme {
  const {
    fontFaces
  } = theme as {
    fontFaces?: unknown;
  };
  if (fontFaces === undefined) {
    return theme as AppTheme;
  }
  if (Array.isArray(fontFaces) && fontFaces.every(f => typeof f === 'string')) {
    return theme as AppTheme;
  }
  return {
    ...theme,
    fontFaces: Array.isArray(fontFaces) ? fontFaces.filter((f): f is string => typeof f === 'string') : undefined
  } as AppTheme;
}

/**
 * Picks the theme to render, in order:
 * 1. the preset chosen in the DEV config selector (dev tools only);
 * 2. the tenant's theme from `/appsettings`, resolved by the white-label proxy;
 * 3. with dev tools on, the default preset, so local and e2e runs without a
 *    themed tenant keep a recognisable look; otherwise an empty theme, which
 *    renders `generateTheme`'s neutral defaults.
 */
export function resolveThemeConfig({
  appSettings,
  override,
  devTools = devToolsEnabled
}: {
  appSettings: AppSettingsDto | null;
  override: unknown;
  devTools?: boolean;
}): ThemeConfig {
  if (devTools && isThemePresetId(override)) {
    return themePresets[override];
  }
  // A tenant can keep the default light scheme and override only the dark one.
  if (hasEntries(appSettings?.theme) || hasEntries(appSettings?.darkTheme)) {
    return {
      theme: sanitizeTheme(appSettings.theme ?? {}),
      darkTheme: hasEntries(appSettings.darkTheme) ? sanitizeTheme(appSettings.darkTheme) : undefined
    };
  }
  return devTools ? themePresets.default : {
    theme: {}
  };
}

/** The DEV config selector's choice; `null` means the tenant's own theme. Read
 *  after mount, so the first paint always matches the server-rendered theme. */
export function useThemeOverride() {
  const [stored, setStored] = useLocalStorage<ThemePresetId | null>({
    key: '@themeOverride'
  });
  const override = devToolsEnabled && isThemePresetId(stored) ? stored : null;
  return [override, setStored] as const;
}
export type ColorSchemeOverride = 'light' | 'dark';
const isColorScheme = (value: unknown): value is ColorSchemeOverride => value === 'light' || value === 'dark';

/** The DEV config selector's mode; `null` follows the theme, and the browser
 *  when the theme ships both schemes. */
export function useColorSchemeOverride() {
  const [stored, setStored] = useLocalStorage<ColorSchemeOverride | null>({
    key: '@colorSchemeOverride'
  });
  const override = devToolsEnabled && isColorScheme(stored) ? stored : null;
  return [override, setStored] as const;
}
export function useThemeConfig(): ThemeConfig {
  const {
    appSettings
  } = useAppState();
  const [override] = useThemeOverride();
  return useMemo(() => resolveThemeConfig({
    appSettings,
    override
  }), [appSettings, override]);
}